import { fetchWithAuth } from "@/lib/http";
import { LogType } from "@/lib/logTypes";

const API_BASE = "/api";

export interface CreateRechargeOrderParams {
  amount: number;
  pay_type: "alipay" | "cryptomus" | "stripe" | "wechat" | "redeem";
  device?: "pc" | "mobile";
  code?: string; // 兑换码（当 pay_type 为 redeem 时使用）
}

export interface CreateRechargeOrderResult {
  order_id: string;
  pay_url?: string; // 支付宝二维码URL
  qr_code?: string; // 支付宝二维码内容（兼容）
  form?: string; // 支付宝表单
  code_url?: string; // 微信Native扫码支付二维码内容
  h5_url?: string; // 微信H5支付跳转链接
}

export interface RechargeRecord {
  id: string;
  amount: number;
  paymentAmount: number;
  paymentMethod: "支付宝" | "微信" | "银行卡";
  orderNo: string;
  status: "pending" | "completed" | "failed";
  createdTime: string;
}

export const createRechargeOrder = async (
  params: CreateRechargeOrderParams
): Promise<CreateRechargeOrderResult> => {
  const endpointMap: Record<string, string> = {
    alipay: "/recharge/create",
    cryptomus: "/recharge/create/cryptomus",
    stripe: "/recharge/create/stripe",
    wechat: "/recharge/create/wechat",
    redeem: "/recharge/redeem",
  };
  const endpoint = endpointMap[params.pay_type] || "/recharge/create";

  const res = await fetchWithAuth(`${API_BASE}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    // 优先解析 JSON，以提供更清晰的错误信息
    let message = "创建充值订单失败";
    try {
      const data = await res.json();
      const detailMsg = (data && data.detail) as string | undefined;
      const errorMsg = (data && data.error) as string | undefined;
      const genericMsg = (data && data.message) as string | undefined;
      // 优先展示 detail，其次通用 message，最后 error
      if (detailMsg && typeof detailMsg === "string") {
        message = detailMsg;
      } else if (genericMsg && typeof genericMsg === "string") {
        message = genericMsg;
      } else if (errorMsg && typeof errorMsg === "string") {
        message = errorMsg;
      }
    } catch {
      const text = await res.text();
      if (text) message = text;
    }
    throw new Error(`${message}${typeof res.status === "number" ? ` (HTTP ${res.status})` : ""}`);
  }

  return res.json();
};

export const getRechargeRecords = async (): Promise<RechargeRecord[]> => {
  try {
    // 查询近2个月的充值记录
    const now = new Date();
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());

    const params = new URLSearchParams();
    params.append("page", "1");
    params.append("page_size", "10000");
    params.append("type", String(LogType.RECHARGE));
    params.append("start_timestamp", String(Math.floor(twoMonthsAgo.getTime() / 1000)));
    params.append("end_timestamp", String(Math.floor(now.getTime() / 1000)));

    const resLog = await fetchWithAuth(`${API_BASE}/log?${params.toString()}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (resLog.ok) {
      const json = await resLog.json();
      const items = json?.data?.items || [];

      const records: RechargeRecord[] = (items as any[])
        .map(it => {
          // 尝试从不同字段解析金额与订单号
          const contentStr = String(it.content ?? it.description ?? "");

          // 优先解析明确标注的「充值金额」字段（如：充值金额：0.14）
          const rechargeAmountMatch = contentStr.match(/充值金额[:：]\s*([0-9]+(?:\.[0-9]+)?)/);
          // 优先解析「增加额度」（如：增加额度：＄0.142856）作为显示的充值额度
          const quotaIncreaseMatch = contentStr.match(
            /增加额度[:：]\s*[\$＄￥]?\s*([0-9]+(?:\.[0-9]+)?)/
          );

          const paymentAmount =
            (rechargeAmountMatch && Number(rechargeAmountMatch[1])) ||
            Number(it.cost_amount ?? it.costAmount ?? it.amount ?? it.quota ?? 0) ||
            Number(contentStr.match(/[\$＄￥]?\s*([0-9]+(?:\.[0-9]+)?)/)?.[1]) ||
            0;

          const amount = (quotaIncreaseMatch && Number(quotaIncreaseMatch[1])) || paymentAmount;

          // 如果 order id 嵌在 content 中（如：备注：ORDER_xxx），优先提取
          const orderFromContent = (contentStr.match(/(ORDER_[\w-]+)/i) || [])[1] || "";
          const orderNo =
            it.order_no ??
            it.orderNo ??
            it.order_id ??
            it.biz_no ??
            it.out_trade_no ??
            orderFromContent ??
            "";

          const paymentMethod = (it.payment_method || it.pay_type || "支付宝") as
            | "支付宝"
            | "微信"
            | "银行卡";

          const status = it.status === "failed" || it.status === "error" ? "failed" : "completed";

          const created = it.timestamp
            ? String(it.timestamp)
            : it.created_at
            ? new Date(Number(it.created_at) * 1000).toLocaleString()
            : new Date().toLocaleString();

          return {
            id: String(it.id ?? orderNo ?? Math.random().toString(36).slice(2)),
            amount: amount,
            paymentAmount: paymentAmount,
            paymentMethod: (paymentMethod as any) || "支付宝",
            orderNo: String(orderNo || ""),
            status: status as "pending" | "completed" | "failed",
            createdTime: created,
          };
        });

      if (records.length > 0) return records;
    }
  } catch (err) {
    // 忽略日志读取错误，尝试回退到专门接口
  }

  return [];
};
